import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { DistrictHeaders, DistrictProps } from "../../enums/district-headers";
import { DistrictManagementService } from "../../services/district-management.service";
import { InputType } from '@core/enums/input-type';
import { AuthService } from "@core/services/auth.service";
import { Role } from "@core/enums/roles";
import { distinctUntilChanged, Observable, take, tap } from "rxjs";
import { Router } from "@angular/router";
import { NzModalService } from "ng-zorro-antd/modal";
import { ToastService } from "@core/services/toast.service";
import { IDistrict, ISearchDistrictRequest, ISearchDistrictResponse } from "../../interfaces/isearchDistrict";
import { ISchool } from "../../interfaces/isearchSchool";
import { IColumnSort } from '@core/interfaces/itable';


@Component({
  selector: 'prism-district-management',
  templateUrl: './district-management.component.html',
  styleUrl: './district-management.component.scss'
})
export class DistrictManagementComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private districtService= inject(DistrictManagementService);
  private authService = inject(AuthService);
  private modal = inject(NzModalService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  searchForm!: FormGroup;
  districtForm!: FormGroup;
  columns = ['name', 'schoolCount', 'countyName'];
  data: any[] = [];
  colHeaders = DistrictHeaders;
  DistrictProps = DistrictProps;
  roles = Role;
  pageIndex = 1;
  pageSize = 10;
  totalData = 0;
  isAddEditDistrict: boolean = false;
  currentUser = this.authService.getCurrentUser();
  currentDistrict = this.authService.getCurrentDistrict();
  isDistrictAdmin = this.currentUser.roleId === Role.DistrictAdmin;

  InputType = InputType;
  modalTitle: string = 'Add District';

  @ViewChild('districtCloseButton') districtCloseButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog?: ElementRef<HTMLElement>;
  private previouslyFocusedElement: HTMLElement | null = null;

  deleteConfirmVisible = false;
  deleteConfirmTitle = '';
  deleteConfirmContent = '';
  private pendingDeleteAction: (() => void) | null = null;
  get search() { return this.searchForm.get('search'); }
  get districtId() { return this.searchForm.get('districtId'); }
  get name() { return this.districtForm.get('name'); }
  get countyId() { return this.districtForm.get('countyId'); }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      search: [null],
      districtId: [this.isDistrictAdmin ? this.currentDistrict?.id : null],
      sortDirection: ['asc'],
      sortColumn: [null]
    }); 

    this.districtForm = this.fb.group({
      id: [0],
      name: [null, [Validators.required, Validators.maxLength(150)]],
      countyId: [null, [Validators.required]]
    }); 
    this.getDistricts();  
    this.listenOrganization();
  }

  listenOrganization(): void {
      this.districtId?.valueChanges.pipe(
        distinctUntilChanged(),
        tap((value: number) => {
          this.districtId?.setValue(value);
          this.authService.setCurrentDistrict(this.districtId?.value);
          this.getDistricts();
        })
      ).subscribe();
  
      this.districtId?.valueChanges.subscribe(() => {
        const districtIdControl = this.districtId;
        if (districtIdControl && districtIdControl.hasError('required')) {
            if (districtIdControl.value !== null && districtIdControl.value !== '' && districtIdControl.value.length > 0) {
                districtIdControl.setErrors(null);
            }
        }
      });
    }

  getDistricts() {
    let payload: ISearchDistrictRequest = {
      ...this.searchForm.value,
      pageIndex: this.pageIndex,
      itemsPerPage: this.pageSize
    };
    this.districtService.searchDistricts(payload).subscribe((response: ISearchDistrictResponse) => {
      this.data = response.items;
      this.pageIndex = response.pageIndex;
      this.totalData = response.totalData;
    });
  }

  addDistrict() {
    this.modalTitle = 'Add District';
    this.districtForm.reset({
      id: 0,
      name: null,
      countyId: null
    });
    this.openDistrictModal();
  }

  editDistrict(district: IDistrict) {
    this.modalTitle = 'Edit District';
    this.districtForm.reset({
      id: district.id,
      name: district.name,
      countyId: district.countyId
    });
    this.openDistrictModal();
  }

  private openDistrictModal(): void {
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.isAddEditDistrict = true;
    this.lockBodyScroll(true);
    setTimeout(() => this.districtCloseButton?.nativeElement.focus());
  }

  deleteDistrict(district: IDistrict) {
    this.deleteConfirmTitle = `Delete District: ${district.name}`;
    this.deleteConfirmContent = 'Are you sure you want to delete this district?';
    this.pendingDeleteAction = () => {
      this.districtService.deleteDistrict(district.id).pipe(
        tap((res: ISchool[]) => {
          if (res) {
            if (res.length === 0) {
              this.toastService.success('District deleted successfully');
              this.getDistricts();
            } else {
              this.toastService.error('Associated schools must be moved or deleted before this district can be removed.');
            }
          }
        })
      ).subscribe();
    };
    this.previouslyFocusedElement = document.activeElement as HTMLElement;
    this.deleteConfirmVisible = true;
    this.lockBodyScroll(true);
    setTimeout(() => this.deleteConfirmDialog?.nativeElement.focus());
  }

  confirmDelete(): void {
    const action = this.pendingDeleteAction;
    this.pendingDeleteAction = null;
    this.deleteConfirmVisible = false;
    this.lockBodyScroll(false);
    this.restorePreviousFocus();
    action?.();
  }

  cancelDelete(): void {
    this.pendingDeleteAction = null;
    this.deleteConfirmVisible = false;
    this.lockBodyScroll(false);
    this.restorePreviousFocus();
  }

  handleColumnDirectionChange(columnSort: IColumnSort): void {
    this.searchForm.patchValue({
      sortDirection: columnSort.direction,
      sortColumn: columnSort.column
    });
    this.getDistricts();
  }

  handlePageIndexChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.getDistricts();
  }

    
  handlePageSizeChange(pageSize: number): void {
    if (pageSize == null || pageSize === this.pageSize) return;
    this.pageSize = pageSize;
    this.getDistricts();
  }

  handleCancel(): void {
    this.isAddEditDistrict = false;
    this.lockBodyScroll(false);
    this.restorePreviousFocus();
  }

  handleSave(): void {
    if (this.districtForm.invalid) {
      this.toastService.error('Please fill out all required fields.');
      return;
    }
    if (this.districtForm.value.id != 0) {
      this.processDistrict(this.districtService.updateDistrict(this.districtForm.value, 'District updated successfully'));
    } else {
      this.processDistrict(this.districtService.addDistrict(this.districtForm.value, 'District added successfully'));
    }
  }

  processDistrict(districtObservable: Observable<boolean>): void {
    districtObservable.pipe(take(1)).subscribe((success: boolean) => {
      if (success) {
        this.isAddEditDistrict = false;
        this.lockBodyScroll(false);
        this.restorePreviousFocus();
        this.getDistricts();
      }
    });
  }

  navigate(item: IDistrict) {
    this.router.navigate(['/school-management', item.id]);
  }

  onDialogKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.handleCancel();
      return;
    }
    this.trapTab(event, dialog);
  }

  onDeleteConfirmKeydown(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelDelete();
      return;
    }
    this.trapTab(event, dialog);
  }

  private trapTab(event: KeyboardEvent, dialog: HTMLElement): void {
    if (event.key !== 'Tab') return;
    const focusableElements = this.getFocusableElements(dialog);
    if (!focusableElements.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private restorePreviousFocus(): void {
    setTimeout(() => {
      this.previouslyFocusedElement?.focus();
      this.previouslyFocusedElement = null;
    });
  }

  private getFocusableElements(container: HTMLElement): HTMLElement[] {
    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'textarea:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
      (element) => !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden')
    );
  }

  private lockBodyScroll(lock: boolean): void {
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  ngOnDestroy(): void {
    this.lockBodyScroll(false);
  }
}